import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useContracts, useConvenios, useTarifas, useComisionConfig, useComisionLedger, useData } from '../contexts/DataContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Input } from '../components/input';
import { Button } from '../components/button';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../components/table';
import { ContratoView } from '../components/ContratoView';
import { FianzaView } from '../components/FianzaView';
import SaldosClienteCard from '../components/SaldosClienteCard';
import AssignedUsersBubbles from '../components/AssignedUsersBubbles';
import UserAssignmentSelector from '../components/UserAssignmentSelector';
import { Dialog, DialogTitle, DialogBody } from '../components/dialog';
import {
  calculateTarifa,
  getPrimaAmount,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  getFacturasVigentes,
  toTimestamp,
} from '../utils/calculations';
import { getFianzaStatus, computePendingFianzas, getFianzaPorcentaje } from '../utils/convenioCompliance';
import ContratoFianzasPanel from '../components/convenio/ContratoFianzasPanel';
import {
  ensureFacturas,
  getEventoTipos,
  ENDOSO_TIPOS,
  getEndososAbiertos,
  clasificarEndosoPendiente,
  ESTADO_FIANZA,
} from '../utils/fianzaEventos';
import { indexMovementsByFianza, resolveComision } from '../utils/comisionReconcile';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../hooks/useFrozenColumns';
import { useUser } from '../components/UserContext';
import { logBitacora, computeUserAssignmentChanges } from '../utils/bitacora';
import {
  PencilSquareIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const ENDOSO_SET = new Set(ENDOSO_TIPOS);
const URGENCIA_ORDER = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const STATUS_BADGE = {
  vigente: 'bg-green-100 text-green-800',
  vencida: 'bg-red-100 text-red-800',
  pendiente: 'bg-gray-100 text-gray-800',
  cancelada: 'bg-zinc-200 text-zinc-700',
  anulada: 'bg-zinc-200 text-zinc-700',
  sustituida: 'bg-zinc-200 text-zinc-700',
};

const PAGO_BADGE = {
  pagada: 'bg-green-100 text-green-800',
  parcial: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-orange-100 text-orange-800',
};

const PAGO_ORDER = { pendiente: 0, parcial: 1, pagada: 2 };

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('es-MX') : 'N/A');

const pagoLabel = (row) =>
  row.pagoEstado === 'pagada'
    ? 'Pagada'
    : row.pagoEstado === 'parcial'
    ? `Parcial ${row.pagoPagadas}/${row.pagoTotal}`
    : 'Pendiente';

// The sucesora/antecesora of a sustitución or renovación, so the row can jump
// to the fianza that replaced it (or the one it replaced).
const relacionChip = (fianza) => {
  if (fianza.sustituidaPor) return { label: 'Ver sustituta', targetId: fianza.sustituidaPor };
  if (fianza.renovadaPor) return { label: 'Ver renovación', targetId: fianza.renovadaPor };
  if (fianza.sustituyeA) return { label: 'Sustituye anterior', targetId: fianza.sustituyeA };
  if (fianza.renuevaA) return { label: 'Renueva anterior', targetId: fianza.renuevaA };
  return null;
};

// Per-contract fianza rollup: lifecycle counts, montos, endosos pendientes,
// and the per-fianza rows the expandable panel renders. Terminal fianzas
// count as "terminadas" and stay out of the monto/prima totals.
// Mirrors the rollup the main Contratos tab builds.
const buildRollup = (contrato, now) => {
  const fianzas = contrato.fianzas || [];
  const r = {
    emitidas: fianzas.length,
    vigentes: 0,
    porVencer: 0,
    vencidas: 0,
    porIniciar: 0,
    terminadas: 0,
    montoAfianzado: 0,
    primaVigente: 0,
    endososPendientes: 0,
    endososUrgencia: null,
    // The requirements themselves, enriched with dueDate/isOverdue/isFutura —
    // the expandable panel lists them, so a count alone isn't enough.
    pendientes: computePendingFianzas([contrato]),
    documentos: (contrato.documentos || []).length,
    rows: [],
  };
  r.reqPendientes = r.pendientes.length;

  fianzas.forEach((fianza) => {
    const status = getFianzaStatus(fianza, now);
    const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
    const prima = getPrimaAmount(fianza) || 0;

    const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
    const pagadas = facturasVigentes.filter(f => f.pagada).length;
    const pagoEstado =
      facturasVigentes.length > 0 && pagadas === facturasVigentes.length
        ? 'pagada'
        : pagadas > 0
        ? 'parcial'
        : 'pendiente';

    const abiertos = status.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(fianza) : [];
    abiertos.forEach(p => {
      const u = clasificarEndosoPendiente(p, now).code;
      if (!r.endososUrgencia || URGENCIA_ORDER[u] < URGENCIA_ORDER[r.endososUrgencia]) {
        r.endososUrgencia = u;
      }
    });
    r.endososPendientes += abiertos.length;

    if (status.estado === ESTADO_FIANZA.ACTIVA) {
      r.montoAfianzado += Number(fianza.montoAfianzado) || 0;
      if (status.code === 'vigente') {
        r.vigentes += 1;
        r.primaVigente += prima;
        if (fin > 0 && fin - now <= THIRTY_DAYS) r.porVencer += 1;
      } else if (status.code === 'vencida') {
        r.vencidas += 1;
      } else {
        r.porIniciar += 1;
      }
    } else {
      r.terminadas += 1;
    }

    r.rows.push({
      fianza: { ...fianza, porcentajeInfo: getFianzaPorcentaje(fianza, contrato) },
      status,
      prima,
      pagoEstado,
      pagadas,
      facturasTotal: facturasVigentes.length,
      endososPendientes: abiertos.length,
    });
  });

  return r;
};

const FianzaBadges = ({ rollup }) => {
  if (rollup.emitidas === 0 && rollup.reqPendientes === 0) {
    return <Text className="text-sm text-zinc-400">Sin fianzas</Text>;
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        {rollup.vigentes > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            {rollup.vigentes} vig.
          </span>
        )}
        {rollup.porVencer > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
            {rollup.porVencer} por vencer
          </span>
        )}
        {rollup.vencidas > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            {rollup.vencidas} venc.
          </span>
        )}
        {rollup.porIniciar > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
            {rollup.porIniciar} por iniciar
          </span>
        )}
        {rollup.terminadas > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">
            {rollup.terminadas} term.
          </span>
        )}
      </div>
      {rollup.reqPendientes > 0 && (
        <Text className="text-xs font-medium text-orange-600">
          {rollup.reqPendientes} por emitir
        </Text>
      )}
      {rollup.endososPendientes > 0 && (
        <span className={`flex items-center gap-1 text-xs font-medium ${
          rollup.endososUrgencia === 'vencido' || rollup.endososUrgencia === 'urgente'
            ? 'text-red-600'
            : 'text-amber-600'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            rollup.endososUrgencia === 'vencido' || rollup.endososUrgencia === 'urgente'
              ? 'bg-red-500'
              : 'bg-amber-500'
          }`}></span>
          {rollup.endososPendientes === 1
            ? 'Endoso pendiente'
            : `${rollup.endososPendientes} endosos pendientes`}
        </span>
      )}
    </div>
  );
};

const ClientContratosYFianzas = ({ clientId, clientData }) => {
  const { contractsList, contractsLoading } = useContracts();
  const { conveniosById } = useConvenios();
  const { tarifas } = useTarifas();
  const { config: comisionConfig } = useComisionConfig();
  const { movements } = useComisionLedger();
  const { updateContractAssignedUsers } = useData();
  const { user, userData, getUserName, isOwner } = useUser();

  const [activeView, setActiveView] = useState('contratos'); // 'contratos' or 'fianzas'
  // Freeze panes. Keyed on activeView: the table unmounts when the view flips,
  // so the measured widths have to be taken again.
  // Contratos: the chevron and No. Contrato columns stay put.
  const contratosFrozen = useFrozenColumns(2, activeView);
  const fianzasFrozen = useFrozenColumns(1, activeView);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterFianzas, setFilterFianzas] = useState('all');
  const [selectedEstatus, setSelectedEstatus] = useState('all');
  const [selectedPago, setSelectedPago] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Contrato view modal
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);

  // Fianza view modal
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);

  // Responsables del contrato
  const [selectedContratoForAssignment, setSelectedContratoForAssignment] = useState(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);

  const [estatusTooltip, setEstatusTooltip] = useState({ visible: false, x: 0, y: 0 });
  const estatusRef = useRef(null);

  // Reset to first page when view, search or filters change
  const resetGuardRef = useRef(true);
  useEffect(() => {
    if (resetGuardRef.current) {
      resetGuardRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, activeView, sortBy, sortOrder, filterFianzas, selectedEstatus, selectedPago]);

  // Filter contracts for this client
  const clientContracts = useMemo(() => {
    return contractsList.filter(contrato => contrato.clientId === clientId);
  }, [contractsList, clientId]);

  const rollups = useMemo(() => {
    const now = Date.now();
    const map = {};
    clientContracts.forEach((contrato) => {
      map[contrato.id] = buildRollup(contrato, now);
    });
    return map;
  }, [clientContracts]);

  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);

  const clienteResponsables = useMemo(() => clientData?.assignedUsers || [], [clientData]);

  // Contratos: filter + sort, same rules the main Contratos tab applies.
  const filteredContratos = useMemo(() => {
    let filtered = clientContracts;

    if (filterFianzas === 'pendientes') {
      filtered = filtered.filter(contrato =>
        contrato.fianzasRequeridas?.value?.some(fr => !fr.fianzaId)
      );
    } else if (filterFianzas === 'endosos') {
      filtered = filtered.filter(contrato => (rollups[contrato.id]?.endososPendientes || 0) > 0);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(contrato => {
        const numeroContrato = contrato.numeroContrato?.value?.toLowerCase() || '';
        const contratoTitle = contrato.tituloContrato?.value?.toLowerCase() || '';
        const contratante = contrato.contratante?.value?.toLowerCase() || '';
        const beneficiario = contrato.beneficiario?.value?.toLowerCase() || '';
        return numeroContrato.includes(searchLower) ||
               contratoTitle.includes(searchLower) ||
               contratante.includes(searchLower) ||
               beneficiario.includes(searchLower);
      });
    }

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'monto':
          comparison = (a.montoContrato?.value || 0) - (b.montoContrato?.value || 0);
          break;
        case 'titulo':
          comparison = (a.tituloContrato?.value || '').localeCompare(b.tituloContrato?.value || '');
          break;
        case 'numeroContrato':
          comparison = (a.numeroContrato?.value || '').localeCompare(b.numeroContrato?.value || '');
          break;
        case 'fianzasRequeridas':
          comparison = (a.fianzasRequeridas?.value?.length || 0) - (b.fianzasRequeridas?.value?.length || 0);
          break;
        case 'fianzasEmitidas':
          comparison = (a.fianzas?.length || 0) - (b.fianzas?.length || 0);
          break;
        default:
          comparison = (a.createdAt?.toMillis?.() || a.createdAt || 0) - (b.createdAt?.toMillis?.() || b.createdAt || 0);
      }

      comparison = sortOrder === 'asc' ? comparison : -comparison;
      if (comparison === 0) return (a.id || '').localeCompare(b.id || '');
      return comparison;
    });
  }, [clientContracts, rollups, filterFianzas, searchTerm, sortBy, sortOrder]);

  // Every fianza of this cliente, enriched once: lifecycle status, per-factura
  // pago, endosos, and ledger-reconciled comisión — the same enrichment the
  // main Fianzas tab does, so both tables agree.
  const allFianzas = useMemo(() => {
    const now = Date.now();
    const out = [];
    clientContracts.forEach(contrato => {
      if (!contrato.fianzas || contrato.fianzas.length === 0) return;
      const splitOpts = {
        contractData: contrato,
        orgConfig: comisionConfig,
        convenioData: contrato.convenioId ? conveniosById?.[contrato.convenioId] : null,
      };

      contrato.fianzas.forEach(fianza => {
        const status = getFianzaStatus(fianza, now);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const vigencia = {
          inicio: toTimestamp(fianza.inicioVigenciaFianza),
          fin: toTimestamp(fianza.finVigenciaFianza),
        };

        const { prima, comisionMonto, comisionPct, pctEstimado } = resolveComision(
          fianza,
          byFianza.get(fianza.id),
          tarifas,
          getEmisorNombre(fianza),
          getSubRamo(fianza),
          getTipoFianza(fianza),
          splitOpts
        );
        const tarifaCalculada = calculateTarifa(prima, montoAfianzado, { vigencia });

        const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
        const pagoTotal = facturasVigentes.length;
        const pagoPagadas = facturasVigentes.filter(f => f.pagada).length;
        const pagoEstado =
          pagoTotal > 0 && pagoPagadas === pagoTotal ? 'pagada' : pagoPagadas > 0 ? 'parcial' : 'pendiente';

        const endososCount = (fianza.eventos || []).filter(e =>
          getEventoTipos(e).some(t => ENDOSO_SET.has(t))
        ).length;
        const abiertos = status.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(fianza) : [];
        let endososUrgencia = null;
        abiertos.forEach(p => {
          const u = clasificarEndosoPendiente(p, now).code;
          if (!endososUrgencia || URGENCIA_ORDER[u] < URGENCIA_ORDER[endososUrgencia]) endososUrgencia = u;
        });

        out.push({
          ...fianza,
          contratoId: contrato.id,
          contratoTitle: contrato.tituloContrato?.value || 'Sin título',
          contratoNumero: contrato.numeroContrato?.value || '',
          clientId: contrato.clientId,
          convenioId: contrato.convenioId || null,
          numeroPoliza: fianza.numeroFianza || 'N/A',
          tipoFianza: fianza.tipo || fianza.tipoFianza || 'N/A',
          contractAssignedUsers: contrato.assignedUsers || [],
          status,
          estatusCalculado: status.code,
          tarifaCalculada,
          primaResuelta: prima,
          comisionMonto,
          comisionPct,
          pctEstimado,
          pagoEstado,
          pagoPagadas,
          pagoTotal,
          endososCount,
          endososPendientesCount: abiertos.length,
          endososUrgencia,
        });
      });
    });
    return out;
  }, [clientContracts, conveniosById, tarifas, byFianza, comisionConfig]);

  const fianzasData = useMemo(() => {
    let fianzas = allFianzas;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      fianzas = fianzas.filter(f =>
        (f.numeroPoliza?.toLowerCase() || '').includes(searchLower) ||
        (f.tipoFianza?.toLowerCase() || '').includes(searchLower) ||
        f.contratoTitle.toLowerCase().includes(searchLower) ||
        f.contratoNumero.toLowerCase().includes(searchLower)
      );
    }
    if (selectedEstatus !== 'all') {
      fianzas = fianzas.filter(f => f.estatusCalculado === selectedEstatus);
    }
    if (selectedPago !== 'all') {
      fianzas = fianzas.filter(f => f.pagoEstado === selectedPago);
    }

    return [...fianzas].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'inicioVigencia':
          comparison = (a.inicioVigenciaFianza || 0) - (b.inicioVigenciaFianza || 0);
          break;
        case 'finVigencia':
          comparison = (a.finVigenciaFianza || 0) - (b.finVigenciaFianza || 0);
          break;
        case 'monto':
          comparison = (a.monto || 0) - (b.monto || 0);
          break;
        case 'numeroPoliza':
          comparison = (a.numeroPoliza || '').localeCompare(b.numeroPoliza || '');
          break;
        case 'estatus':
          comparison = (a.estatusCalculado || '').localeCompare(b.estatusCalculado || '');
          break;
        case 'pago':
          comparison = PAGO_ORDER[a.pagoEstado] - PAGO_ORDER[b.pagoEstado];
          break;
        default:
          comparison = (a.createdAt || 0) - (b.createdAt || 0);
      }

      comparison = sortOrder === 'asc' ? comparison : -comparison;
      if (comparison === 0) {
        return `${a.contratoId}-${a.id || ''}`.localeCompare(`${b.contratoId}-${b.id || ''}`);
      }
      return comparison;
    });
  }, [allFianzas, searchTerm, selectedEstatus, selectedPago, sortBy, sortOrder]);

  // Determine which data to paginate based on active view
  const dataToDisplay = activeView === 'contratos' ? filteredContratos : fianzasData;
  const totalPages = Math.ceil(dataToDisplay.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = dataToDisplay.slice(startIndex, endIndex);

  // Clamp page if data shrinks
  useEffect(() => {
    if (contractsLoading) return;
    if (dataToDisplay.length === 0) return;
    const maxPage = Math.max(1, Math.ceil(dataToDisplay.length / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [dataToDisplay.length, itemsPerPage, currentPage, contractsLoading]);

  // Handlers
  const handleContratoClick = (contrato) => {
    setSelectedContrato(contrato);
    setIsContratoViewOpen(true);
  };

  const handleContratoViewClose = () => {
    setIsContratoViewOpen(false);
    setSelectedContrato(null);
  };

  const handleFianzaClick = useCallback((fianza) => {
    setSelectedFianza(fianza);
    setIsFianzaViewOpen(true);
  }, []);

  const handleFianzaViewClose = () => {
    setIsFianzaViewOpen(false);
    setSelectedFianza(null);
  };

  const toggleExpanded = (contratoId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  };

  // The expandable panel hands over a raw fianza; open the enriched row so the
  // modal sees the same data the fianzas table would give it.
  const openFianza = (contrato, fianza) => {
    const enriched = allFianzas.find(f => f.contratoId === contrato.id && f.id === fianza.id);
    setSelectedFianza(enriched || { ...fianza, contratoId: contrato.id, clientId: contrato.clientId });
    setIsFianzaViewOpen(true);
  };

  // Open the sucesora/antecesora of a sustitución or renovación (same contrato).
  const openRelatedFianza = (row, targetFianzaId) => {
    if (!targetFianzaId) return;
    const enriched = allFianzas.find(f => f.contratoId === row.contratoId && f.id === targetFianzaId);
    if (!enriched) return;
    setSelectedFianza(enriched);
    setIsFianzaViewOpen(true);
  };

  if (contractsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const selectClass = 'px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm';

  return (
    <div className="space-y-6">
      {/* What this cliente owes its afianzadoras, and what they hold for it.
          Renders nothing when there is neither debt nor credit. */}
      <SaldosClienteCard contracts={clientContracts} clientId={clientId} onFianzaClick={handleFianzaClick} />

      {/* Header with View Toggle Buttons */}
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div>
            <Heading level={2} className="text-azul-surelia">Contratos y Fianzas</Heading>
            <Text className="text-zinc-600 text-sm">
              {activeView === 'contratos'
                ? `Visualiza ${filteredContratos.length} contrato${filteredContratos.length !== 1 ? 's' : ''}`
                : `Visualiza ${fianzasData.length} fianza${fianzasData.length !== 1 ? 's' : ''}`
              }
            </Text>
          </div>

          {/* View Toggle Buttons */}
          <div className="flex gap-2">
            <Button
              color={activeView === 'contratos' ? 'surelia' : 'white'}
              onClick={() => setActiveView('contratos')}
              className="min-w-[120px]"
            >
              Contratos ({filteredContratos.length})
            </Button>
            <Button
              color={activeView === 'fianzas' ? 'surelia' : 'white'}
              onClick={() => setActiveView('fianzas')}
              className="min-w-[120px]"
            >
              Fianzas ({fianzasData.length})
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          {activeView === 'contratos' ? (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Fianzas:</label>
              <select
                value={filterFianzas}
                onChange={(e) => setFilterFianzas(e.target.value)}
                className={`${selectClass} min-w-[200px]`}
              >
                <option value="all">Todos</option>
                <option value="pendientes">Con requeridas pendientes</option>
                <option value="endosos">Con endosos pendientes</option>
              </select>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Estatus:</label>
                <select
                  value={selectedEstatus}
                  onChange={(e) => setSelectedEstatus(e.target.value)}
                  className={`${selectClass} min-w-[130px]`}
                >
                  <option value="all">Todos</option>
                  <option value="vigente">Vigente</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="vencida">Vencida</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="anulada">Anulada</option>
                  <option value="sustituida">Sustituida</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Pago:</label>
                <select
                  value={selectedPago}
                  onChange={(e) => setSelectedPago(e.target.value)}
                  className={`${selectClass} min-w-[120px]`}
                >
                  <option value="all">Todos</option>
                  <option value="pagada">Pagada</option>
                  <option value="parcial">Parcial</option>
                  <option value="pendiente">Pendiente</option>
                </select>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Mostrar:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className={`${selectClass} min-w-[60px]`}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Ordenar:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`${selectClass} min-w-[140px]`}
            >
              <option value="createdAt">Fecha de creación</option>
              {activeView === 'contratos' ? (
                <>
                  <option value="monto">Monto</option>
                  <option value="numeroContrato">No. Contrato</option>
                  <option value="titulo">Título</option>
                  <option value="fianzasRequeridas">Fianzas requeridas</option>
                  <option value="fianzasEmitidas">Fianzas emitidas</option>
                </>
              ) : (
                <>
                  <option value="numeroPoliza">No. de fianza</option>
                  <option value="monto">Prima</option>
                  <option value="inicioVigencia">Inicio de vigencia</option>
                  <option value="finVigencia">Fin de vigencia</option>
                  <option value="estatus">Estatus</option>
                  <option value="pago">Pago</option>
                </>
              )}
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 rounded-lg border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
              title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              {sortOrder === 'asc' ? (
                <ArrowUpIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          <Input
            type="text"
            placeholder={activeView === 'contratos' ? 'Buscar por no. de contrato, título...' : 'Buscar por fianza, tipo, contrato...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 sm:max-w-xs"
          />
        </div>
      </div>

      {/* Table Display */}
      {activeView === 'contratos' ? (
        // Contratos Table
        <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
          <div className="relative">
            <Table onScroll={contratosFrozen.onScroll}>
              <TableHead className="bg-azul-surelia">
                <TableRow>
                  <TableHeader
                    ref={contratosFrozen.setRef(0)}
                    style={{ left: contratosFrozen.offsets[0] }}
                    className={`text-white font-semibold py-3 border-b-0 w-8 ${frozenHeadClass}`}
                  ></TableHeader>
                  <TableHeader
                    ref={contratosFrozen.setRef(1)}
                    style={{ left: contratosFrozen.offsets[1] }}
                    className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                  >No. Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Responsables</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Fianzas</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Monto Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzado / Prima</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Título del Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Beneficiario</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((contrato, index) => {
                  const rollup = rollups[contrato.id] || { rows: [], emitidas: 0, reqPendientes: 0, documentos: 0 };
                  const expanded = expandedIds.has(contrato.id);
                  return (
                    <React.Fragment key={contrato.id}>
                      <TableRow
                        className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                        }`}
                        onClick={() => handleContratoClick(contrato)}
                      >
                        <TableCell
                          style={{ left: contratosFrozen.offsets[0] }}
                          className={`py-3 pr-0 ${frozenCellClass(index)}`}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(contrato.id);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {expanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>

                        <TableCell
                          style={{ left: contratosFrozen.offsets[1] }}
                          className={`py-3 ${frozenCellClass(index)}`}
                        >
                          {contrato.numeroContrato?.value ? (
                            <Text className="text-sm font-mono font-medium text-zinc-900" title={contrato.numeroContrato.value}>
                              {contrato.numeroContrato.value}
                            </Text>
                          ) : (
                            <Text className="text-sm text-zinc-400">Sin número</Text>
                          )}
                        </TableCell>

                        <TableCell className="py-3">
                          <div className="flex items-center gap-2">
                            <AssignedUsersBubbles
                              userIds={[
                                ...clienteResponsables,
                                ...(contrato.assignedUsers || [])
                              ].filter((v, i, a) => a.indexOf(v) === i)}
                              maxDisplay={3}
                              size="sm"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContratoForAssignment(contrato);
                                setIsAssignmentDialogOpen(true);
                              }}
                              className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                              title="Editar responsables"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>

                        <TableCell className="py-3">
                          <FianzaBadges rollup={rollup} />
                        </TableCell>

                        <TableCell className="py-3">
                          <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                            contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                            contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {contrato.estatus?.value || 'N/A'}
                          </span>
                        </TableCell>

                        <TableCell className="py-3">
                          <Text className="text-sm font-semibold text-azul-surelia">
                            {fmtMXN(contrato.montoContrato?.value || 0)}
                          </Text>
                        </TableCell>

                        <TableCell className="py-3">
                          {rollup.montoAfianzado > 0 || rollup.primaVigente > 0 ? (
                            <div className="text-xs">
                              <div className="text-zinc-700 font-semibold tabular-nums">
                                {fmtMXN(rollup.montoAfianzado)}
                              </div>
                              <div className="text-zinc-500 tabular-nums">
                                Prima: {fmtMXN(rollup.primaVigente)}
                              </div>
                            </div>
                          ) : (
                            <Text className="text-sm text-zinc-400">—</Text>
                          )}
                        </TableCell>

                        <TableCell className="py-3">
                          <div className="max-w-xs">
                            <Text
                              className="text-sm text-zinc-900 truncate"
                              title={contrato.tituloContrato?.value || 'Sin título'}
                            >
                              {contrato.tituloContrato?.value || 'Sin título'}
                            </Text>
                          </div>
                        </TableCell>

                        <TableCell className="py-3">
                          <Text className="text-sm text-zinc-900">
                            {contrato.beneficiario?.value || contrato.contratante?.value || 'N/A'}
                          </Text>
                        </TableCell>
                      </TableRow>

                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          {/* Spacer over the frozen columns. The panel is sticky, and at
                              the end of the scroll its containing block shoves it
                              back over the frozen strip — this opaque z-10 cell is
                              what it slides UNDER, instead of crossing the freeze
                              divider. Tint = bg-azul-surelia/5 flattened over white. */}
                          <TableCell colSpan={2} style={{ left: 0 }} className="sticky z-10 bg-[#F4F6F7] p-0" />
                          <TableCell colSpan={7} className="py-3 px-6">
                            {/* Same drill-down the convenio tabs render: every
                                fianza emitida plus a card per requisito still
                                por emitir. */}
                            <ContratoFianzasPanel
                              stats={rollup}
                              contratoId={contrato.id}
                              onFianzaClick={(_contratoId, fianza) => openFianza(contrato, fianza)}
                              canUpload={false}
                              stickyOffset={contratosFrozen.total}
                            />
                            {rollup.documentos > 0 && (
                              <div className="sticky max-w-4xl mt-2" style={{ left: contratosFrozen.total }}>
                                <Text className="text-xs text-zinc-500">
                                  {rollup.documentos} {rollup.documentos === 1 ? 'documento adjunto' : 'documentos adjuntos'}
                                </Text>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan="9" className="py-8 text-center text-zinc-500">
                      No se encontraron contratos
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
                pinned to the boundary instead of riding along with the cells. */}
            {contratosFrozen.scrolled && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                style={{ left: contratosFrozen.dividerLeft, bottom: contratosFrozen.dividerBottom }}
              />
            )}
          </div>
        </div>
      ) : (
        // Fianzas Table
        <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
          <div className="relative">
            <Table onScroll={fianzasFrozen.onScroll}>
              <TableHead className="bg-azul-surelia">
                <TableRow>
                  <TableHeader
                    ref={fianzasFrozen.setRef(0)}
                    style={{ left: fianzasFrozen.offsets[0] }}
                    className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                  >Número de Fianza</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Prima Total</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Tarifa</TableHeader>
                  {isOwner && (
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Comisión</TableHeader>
                  )}
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Vigencia</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">
                    <div className="flex items-center gap-1">
                      Estatus
                      <span
                        ref={estatusRef}
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/60 text-[10px] cursor-help"
                        onMouseEnter={() => {
                          const rect = estatusRef.current.getBoundingClientRect();
                          setEstatusTooltip({ visible: true, x: rect.left + rect.width / 2, y: rect.bottom + 8 });
                        }}
                        onMouseLeave={() => setEstatusTooltip(prev => ({ ...prev, visible: false }))}
                      >?</span>
                    </div>
                  </TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Pago</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo de Fianza</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">No. Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Título del Contrato</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((fianza, idx) => {
                  const relacion = relacionChip(fianza);
                  return (
                    <TableRow
                      key={`${fianza.contratoId}-${fianza.id || idx}`}
                      className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                      }`}
                      onClick={() => handleFianzaClick(fianza)}
                    >
                      <TableCell
                        style={{ left: fianzasFrozen.offsets[0] }}
                        className={`py-3 ${frozenCellClass(idx)}`}
                      >
                        <Text className="text-sm text-zinc-700 font-mono">
                          {fianza.numeroPoliza}
                        </Text>
                        {fianza.endososCount > 0 && (
                          <Text className="text-xs text-zinc-500 mt-0.5">
                            {fianza.endososCount} {fianza.endososCount === 1 ? 'endoso' : 'endosos'}
                          </Text>
                        )}
                        {fianza.endososPendientesCount > 0 && (
                          <span className={`inline-flex items-center gap-1 mt-0.5 text-xs font-medium ${
                            fianza.endososUrgencia === 'vencido' || fianza.endososUrgencia === 'urgente'
                              ? 'text-red-600'
                              : 'text-amber-600'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              fianza.endososUrgencia === 'vencido' || fianza.endososUrgencia === 'urgente'
                                ? 'bg-red-500'
                                : 'bg-amber-500'
                            }`}></span>
                            {fianza.endososPendientesCount === 1
                              ? 'Endoso pendiente'
                              : `${fianza.endososPendientesCount} endosos pendientes`}
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="py-3">
                        <Text className="text-sm font-semibold text-verde-lima">
                          {fmtMXN(fianza.monto || 0)}
                        </Text>
                      </TableCell>

                      <TableCell className="py-3">
                        <Text className="text-sm font-medium text-zinc-900">
                          {fianza.tarifaCalculada !== null ? `${fianza.tarifaCalculada.toFixed(2)}%` : 'N/A'}
                        </Text>
                      </TableCell>

                      {isOwner && (
                        <TableCell className="py-3">
                          {fianza.comisionMonto != null ? (
                            <>
                              <Text className="text-sm font-semibold text-azul-surelia tabular-nums">
                                {fmtMXN(fianza.comisionMonto)}
                              </Text>
                              {fianza.comisionPct != null && (
                                <div className="flex items-center gap-1">
                                  <Text className="text-xs text-zinc-500">{fianza.comisionPct}%</Text>
                                  {/* A convenio % is another agent's deal: only the
                                      explicit stamp means a person confirmed it. A
                                      locked comisionPct does not — legacy uploads
                                      stamped the org's own %. */}
                                  {fianza.convenioId ? (
                                    fianza.comisionPctConfirmada ? (
                                      <span
                                        className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                                        title="El % de comisión de esta fianza de convenio ya fue confirmado."
                                      >
                                        confirmado
                                      </span>
                                    ) : (
                                      <span
                                        className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                                        title="El % de comisión del agente del fiado es estimado; confírmalo al ver el % real."
                                      >
                                        por confirmar
                                      </span>
                                    )
                                  ) : fianza.pctEstimado ? (
                                    <span
                                      className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                                      title="Comisión estimada con la tabla de comisiones por defecto; confírmala al ver el % real."
                                    >
                                      por confirmar
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </>
                          ) : (
                            <span
                              className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                              title="Sin tabla de comisiones para esta afianzadora: la comisión quedó pendiente de %. Créala en Configuración > Comisiones y podrás retarificar."
                            >
                              % por confirmar
                            </span>
                          )}
                        </TableCell>
                      )}

                      <TableCell className="py-3">
                        <div className="text-xs">
                          <div className="text-zinc-600">
                            <span className="font-medium">Inicio:</span> {fmtDate(fianza.inicioVigenciaFianza)}
                          </div>
                          <div className="text-zinc-600">
                            <span className="font-medium">Fin:</span> {fmtDate(fianza.finVigenciaFianza)}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                          STATUS_BADGE[fianza.estatusCalculado] || 'bg-gray-100 text-gray-800'
                        }`}>
                          {fianza.status.label}
                        </span>
                        {relacion && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openRelatedFianza(fianza, relacion.targetId);
                            }}
                            className="mt-1 flex items-center gap-1 text-xs font-medium text-azul-surelia hover:underline"
                          >
                            {relacion.label}
                            <ArrowRightIcon className="w-3 h-3" />
                          </button>
                        )}
                      </TableCell>

                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${PAGO_BADGE[fianza.pagoEstado]}`}>
                          {pagoLabel(fianza)}
                        </span>
                      </TableCell>

                      <TableCell className="py-3">
                        <Text className="text-sm font-semibold text-zinc-900">
                          {fianza.tipoFianza}
                        </Text>
                      </TableCell>

                      <TableCell className="py-3">
                        {fianza.contratoNumero ? (
                          <Text className="text-sm font-mono text-zinc-700" title={fianza.contratoNumero}>
                            {fianza.contratoNumero}
                          </Text>
                        ) : (
                          <Text className="text-sm text-zinc-400">—</Text>
                        )}
                      </TableCell>

                      <TableCell className="py-3">
                        <div className="max-w-xs">
                          <Text
                            className="text-sm text-zinc-900 truncate"
                            title={fianza.contratoTitle}
                          >
                            {fianza.contratoTitle}
                          </Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isOwner ? 10 : 9} className="py-8 text-center text-zinc-500">
                      No se encontraron fianzas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
                pinned to the boundary instead of riding along with the cells. */}
            {fianzasFrozen.scrolled && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                style={{ left: fianzasFrozen.dividerLeft, bottom: fianzasFrozen.dividerBottom }}
              />
            )}
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {dataToDisplay.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-zinc-600">
              Mostrando {startIndex + 1} - {Math.min(endIndex, dataToDisplay.length)} de {dataToDisplay.length} {activeView === 'contratos' ? 'contratos' : 'fianzas'}
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Anterior
              </Button>

              <div className="flex items-center gap-1">
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;

                  if (totalPages <= maxVisiblePages) {
                    for (let i = 1; i <= totalPages; i++) {
                      pageNumbers.push(i);
                    }
                  } else {
                    pageNumbers.push(1);

                    if (currentPage <= 2) {
                      for (let i = 2; i <= Math.min(3, totalPages - 1); i++) {
                        pageNumbers.push(i);
                      }
                      if (totalPages > 3) pageNumbers.push('...');
                    } else if (currentPage >= totalPages - 1) {
                      if (totalPages > 3) pageNumbers.push('...');
                      for (let i = Math.max(totalPages - 2, 2); i <= totalPages - 1; i++) {
                        pageNumbers.push(i);
                      }
                    } else {
                      pageNumbers.push('...');
                      pageNumbers.push(currentPage);
                      pageNumbers.push('...');
                    }

                    if (totalPages > 1) pageNumbers.push(totalPages);
                  }

                  return pageNumbers.map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-zinc-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        color={currentPage === page ? 'surelia' : 'white'}
                        className="text-sm px-3 py-2 min-w-[40px]"
                      >
                        {page}
                      </Button>
                    )
                  ));
                })()}
              </div>

              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Estatus tooltip */}
      {estatusTooltip.visible && ReactDOM.createPortal(
        <div
          className="fixed w-56 p-2.5 bg-zinc-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
          style={{ top: estatusTooltip.y, left: estatusTooltip.x, transform: 'translateX(-50%)', zIndex: 9999 }}
        >
          <p className="font-semibold mb-1">Estatus de la fianza:</p>
          <p><span className="font-medium text-green-400">Vigente:</span> Fecha actual entre inicio y fin</p>
          <p><span className="font-medium text-gray-400">Pendiente:</span> Aún no inicia la vigencia</p>
          <p><span className="font-medium text-red-400">Vencida:</span> Ya pasó la fecha de fin</p>
          <p><span className="font-medium text-zinc-400">Cancelada / Anulada / Sustituida:</span> Fianza terminada; el estatus terminal manda sobre las fechas</p>
        </div>,
        document.body
      )}

      {/* Responsables del contrato */}
      <Dialog
        open={isAssignmentDialogOpen}
        onClose={() => setIsAssignmentDialogOpen(false)}
        size="lg"
      >
        <DialogTitle>Responsables del Contrato</DialogTitle>
        <DialogBody>
          {selectedContratoForAssignment && (
            <UserAssignmentSelector
              assignedUsers={selectedContratoForAssignment.assignedUsers || []}
              lockedUsers={clienteResponsables}
              onSave={async (userIds) => {
                const oldUsers = selectedContratoForAssignment.assignedUsers || [];
                const { description, changes, hasChanges } = computeUserAssignmentChanges(
                  oldUsers,
                  userIds,
                  getUserName
                );

                await updateContractAssignedUsers(selectedContratoForAssignment.id, userIds);

                if (hasChanges) {
                  try {
                    const contratoTitle = selectedContratoForAssignment.tituloContrato?.value || 'Sin título';
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'contracts',
                      documentId: selectedContratoForAssignment.id,
                      changes,
                      metadata: {
                        clientId,
                        clientName: clientData?.nombreEmpresa || 'Sin nombre',
                        contratoId: selectedContratoForAssignment.id,
                        description: `${description} en contrato "${contratoTitle}"`,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log contract assignment to bitacora:', logError);
                  }
                }

                setIsAssignmentDialogOpen(false);
                setSelectedContratoForAssignment(null);
              }}
              onCancel={() => {
                setIsAssignmentDialogOpen(false);
                setSelectedContratoForAssignment(null);
              }}
            />
          )}
        </DialogBody>
      </Dialog>

      {/* ContratoView Modal - Rendered via Portal */}
      {selectedContrato && ReactDOM.createPortal(
        <ContratoView
          isOpen={isContratoViewOpen}
          onClose={handleContratoViewClose}
          contrato={selectedContrato}
          clientData={clientData}
          onUpdate={() => {}}
        />,
        document.body
      )}

      {/* FianzaView Modal - Rendered via Portal */}
      {selectedFianza && ReactDOM.createPortal(
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={handleFianzaViewClose}
          fianza={selectedFianza}
          clientData={clientData}
          contratoData={clientContracts.find(c => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />,
        document.body
      )}
    </div>
  );
};

export default ClientContratosYFianzas;
